import { useState, useEffect, useRef } from 'react';
import { apiCall } from '../widgets.jsx';
import { getSeedTroedelItems, getSeedMarketDays, getSeedActiveSession } from '../test-helpers/seed.js';

/**
 * useHubData — data-loading hook for FleaHub.
 * Covers: items, sessions, activeSession, tab, PTR state, and
 * all loading/pagination functions.
 *
 * sessions fetched with include_stats=1; flea-categories loaded.
 *
 * @param {{ store, authToken, mode: "flea" }} params
 */
export function useHubData({ store, authToken, mode }) {
  const [loading, setLoading]                       = useState(true);
  const [items, setItems]                           = useState([]);
  const [hasMoreItems, setHasMoreItems]             = useState(false);
  const [itemsCursor, setItemsCursor]               = useState(null);
  const [sessions, setSessions]                     = useState([]);
  const [hasMoreSessions, setHasMoreSessions]       = useState(false);
  const [sessionsCursor, setSessionsCursor]         = useState(null);
  const [categories, setCategories]                 = useState([]); // flea only
  const [activeSession, setActiveSession]           = useState(null);
  const [tab, setTab]                               = useState("sessions"); // 'sessions' | 'inventory' | 'stats'
  const [viewingSessionId, setViewingSessionId]     = useState(null);
  const [showHubDuringSession, setShowHubDuringSession] = useState(false);
  const [settingsOpen, setSettingsOpen]             = useState(false);
  const [error, setError]                           = useState(null);
  const [refreshing, setRefreshing]                 = useState(false);
  const ptrStartY                                   = useRef(null);
  const [ptrDistance, setPtrDistance]               = useState(0);

  const apiBase = `/api/stores/${store.id}`;

  const reloadAll = async () => {
    setLoading(true); setError(null);
    // DEV-only seed bypass — tree-shaken in production
    const seedItems    = getSeedTroedelItems();
    const seedSessions = getSeedMarketDays();
    const seedActive   = getSeedActiveSession();
    if (seedItems || seedSessions || seedActive) {
      if (seedItems)    setItems(seedItems);
      if (seedSessions) setSessions(seedSessions);
      setActiveSession(seedActive ?? null);
      setLoading(false);
      return;
    }
    try {
      // include_stats=1 für beide Modi — Markt braucht Revenue für Session-Karten (F1)
      const sessionsUrl = `${apiBase}/sessions?include_stats=1`;
      const requests = [
        apiCall(authToken, `${apiBase}/items`),
        apiCall(authToken, sessionsUrl),
        apiCall(authToken, `${apiBase}/sessions/active`),
      ];
      requests.push(apiCall(authToken, `${apiBase}/flea-categories`).catch(() => []));

      const results = await Promise.all(requests);
      const [itemsR, sessionsR, activeR] = results;
      const categoriesR = results[3] || [];

      const itemsArr = Array.isArray(itemsR) ? itemsR : (itemsR?.items || []);
      setItems(itemsArr);
      setHasMoreItems(!!itemsR?.has_more);
      setItemsCursor(itemsR?.next_cursor || null);

      const sessionsArr = Array.isArray(sessionsR) ? sessionsR : (sessionsR?.sessions || []);
      setSessions(sessionsArr);
      setHasMoreSessions(!!sessionsR?.has_more);
      setSessionsCursor(sessionsR?.next_cursor || null);

      setActiveSession(activeR);
      setCategories(categoriesR);
    } catch (e) {
      console.warn(`${mode}Hub reload failed:`, e);
      setError(e.message || "Lade-Fehler");
    } finally {
      setLoading(false);
    }
  };

  const loadMoreSessions = async () => {
    if (!sessionsCursor) return;
    try {
      const suffix = "&include_stats=1";
      const r = await apiCall(authToken, `${apiBase}/sessions?before=${encodeURIComponent(sessionsCursor)}${suffix}`);
      const arr = Array.isArray(r) ? r : (r?.sessions || []);
      setSessions(prev => [...prev, ...arr]);
      setHasMoreSessions(!!r?.has_more);
      setSessionsCursor(r?.next_cursor || null);
    } catch (e) { console.warn("loadMoreSessions failed:", e); }
  };

  const loadMoreItems = async () => {
    if (!itemsCursor) return;
    try {
      const r = await apiCall(authToken, `${apiBase}/items?before=${encodeURIComponent(itemsCursor)}`);
      const arr = Array.isArray(r) ? r : (r?.items || []);
      setItems(prev => [...prev, ...arr]);
      setHasMoreItems(!!r?.has_more);
      setItemsCursor(r?.next_cursor || null);
    } catch (e) { console.warn("loadMoreItems failed:", e); }
  };

  // Reload on store change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reloadAll(); }, [store?.id]);

  // Body-scroll-lock + reset scroll position so header is always visible
  useEffect(() => {
    window.scrollTo(0, 0);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return {
    loading, setLoading,
    items, setItems, hasMoreItems, itemsCursor,
    sessions, setSessions, hasMoreSessions, sessionsCursor,
    categories, setCategories,
    activeSession, setActiveSession,
    tab, setTab,
    viewingSessionId, setViewingSessionId,
    showHubDuringSession, setShowHubDuringSession,
    settingsOpen, setSettingsOpen,
    error, setError,
    refreshing, setRefreshing,
    ptrStartY, ptrDistance, setPtrDistance,
    reloadAll, loadMoreSessions, loadMoreItems,
  };
}
